import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Check } from 'lucide-react';
import { suiteAPI } from '../lib/api';

export default function PublicLandingPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [error, setError] = useState(false);
  const [form, setForm] = useState({});
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    suiteAPI.publicPage(slug).then(r => setPage(r.data)).catch(() => setError(true));
  }, [slug]);

  useEffect(() => {
    if (page?.seo?.title) document.title = page.seo.title;
  }, [page]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await suiteAPI.submitLead(slug, { fields: form });
      setSent(true);
    } catch { setSending(false); }
  };

  if (error) return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center text-center p-8">
      <div><h1 className="text-2xl font-bold text-white mb-2">Page not found</h1><p className="text-zinc-500">This page is unpublished or the link is wrong.</p></div>
    </div>
  );
  if (!page) return <div className="min-h-screen bg-zinc-950 flex items-center justify-center"><Loader2 className="animate-spin text-[#b8ff00]" /></div>;

  const theme = page.theme || {};
  const bg = theme.background || '#0a0a0a';
  const text = theme.text || '#ffffff';
  const accent = theme.accent || '#b8ff00';

  const scrollToForm = () => document.getElementById('lead-form')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div style={{ background: bg, color: text }} className="min-h-screen" data-testid="public-landing-page">
      {(page.sections || []).map(s => {
        if (s.type === 'hero') return (
          <section key={s.id} className="px-6 py-24 md:py-32 max-w-5xl mx-auto" data-testid="lp-hero">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-3xl">{s.headline}</h1>
            <p className="text-base md:text-lg mt-5 max-w-2xl opacity-70">{s.subheadline}</p>
            <button onClick={scrollToForm} style={{ background: accent, color: '#0a0a0a' }} className="mt-8 px-7 py-3.5 rounded-full font-bold text-sm hover:opacity-90 transition-opacity" data-testid="lp-hero-cta">
              {s.cta_text || 'Get started'}
            </button>
            {s.image_url && <img src={s.image_url} alt="" className="mt-12 w-full rounded-2xl object-cover max-h-[420px]" />}
          </section>
        );
        if (['benefits', 'features'].includes(s.type)) return (
          <section key={s.id} className="px-6 py-16 max-w-5xl mx-auto border-t" style={{ borderColor: `${text}14` }}>
            <h2 className="text-base md:text-lg font-semibold uppercase tracking-widest opacity-60 mb-8">{s.title}</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {(s.items || []).map((it, i) => (
                <div key={i}>
                  <div style={{ background: accent }} className="w-8 h-1 mb-4" />
                  <h3 className="text-base md:text-lg font-semibold">{it.title}</h3>
                  <p className="text-sm opacity-65 mt-2 leading-relaxed">{it.description}</p>
                </div>
              ))}
            </div>
          </section>
        );
        if (s.type === 'testimonials') return (
          <section key={s.id} className="px-6 py-16 max-w-5xl mx-auto border-t" style={{ borderColor: `${text}14` }}>
            <h2 className="text-base md:text-lg font-semibold uppercase tracking-widest opacity-60 mb-8">{s.title}</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {(s.items || []).map((it, i) => (
                <blockquote key={i} className="p-6 rounded-2xl" style={{ background: `${text}0a` }}>
                  <p className="text-sm leading-relaxed">"{it.quote || it.title}"</p>
                  <footer className="text-xs opacity-60 mt-3">— {it.author || it.description}</footer>
                </blockquote>
              ))}
            </div>
          </section>
        );
        if (s.type === 'faq') return (
          <section key={s.id} className="px-6 py-16 max-w-3xl mx-auto border-t" style={{ borderColor: `${text}14` }}>
            <h2 className="text-base md:text-lg font-semibold uppercase tracking-widest opacity-60 mb-8">{s.title}</h2>
            {(s.items || []).map((it, i) => (
              <details key={i} className="py-4 border-b" style={{ borderColor: `${text}14` }}>
                <summary className="text-sm font-medium cursor-pointer">{it.title}</summary>
                <p className="text-sm opacity-65 mt-2">{it.description}</p>
              </details>
            ))}
          </section>
        );
        if (s.type === 'form') return (
          <section key={s.id} id="lead-form" className="px-6 py-20 max-w-xl mx-auto" data-testid="lp-form-section">
            <h2 className="text-2xl font-bold">{s.title || 'Get started'}</h2>
            <p className="text-sm opacity-65 mt-2">{s.subtitle}</p>
            {sent ? (
              <div className="mt-8 p-6 rounded-2xl flex items-center gap-3" style={{ background: `${accent}1a` }} data-testid="lp-form-success">
                <Check size={18} style={{ color: accent }} />
                <p className="text-sm">Thanks — we'll be in touch shortly.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-8 space-y-4">
                {(page.form_fields || []).map(f => (
                  <div key={f.name}>
                    <label className="text-xs uppercase tracking-wider opacity-60">{f.label}</label>
                    <input
                      type={f.type || 'text'}
                      required={f.required}
                      value={form[f.name] || ''}
                      onChange={e => setForm({ ...form, [f.name]: e.target.value })}
                      data-testid={`lp-field-${f.name}`}
                      className="w-full mt-1.5 px-4 py-3 rounded-xl bg-transparent border outline-none text-sm"
                      style={{ borderColor: `${text}33`, color: text }}
                    />
                  </div>
                ))}
                <button type="submit" disabled={sending} style={{ background: accent, color: '#0a0a0a' }} className="w-full py-3.5 rounded-full font-bold text-sm" data-testid="lp-submit">
                  {sending ? 'Sending…' : (s.button_text || 'Submit')}
                </button>
              </form>
            )}
          </section>
        );
        if (s.type === 'cta') return (
          <section key={s.id} className="px-6 py-24 text-center border-t" style={{ borderColor: `${text}14` }}>
            <h2 className="text-3xl md:text-4xl font-bold max-w-2xl mx-auto">{s.headline}</h2>
            <button onClick={scrollToForm} style={{ background: accent, color: '#0a0a0a' }} className="mt-7 px-7 py-3.5 rounded-full font-bold text-sm" data-testid="lp-final-cta">
              {s.button_text || s.cta_text || 'Get started'}
            </button>
          </section>
        );
        return null;
      })}
      <footer className="px-6 py-10 text-center text-[11px] opacity-40">{page.name}</footer>
    </div>
  );
}
